import { useRouter } from 'next/router';

import { searchPageApi } from 'api/searchPageApi';

import type { IGetAdsRequest } from 'api/types/searchPageApiTypes';

import { useSearchPageDispatch } from 'components/SearchPage/context/SearchPageContext';
import { useFiltersState } from 'features/filters/Filters.context';

import { rg4js } from 'helpers/raygun';
import { PAGE } from 'helpers/pages';
import { API_CLIENT_TIMEOUT } from 'utils';
import { fireToast } from 'helpers/Toasts';
import { mapGetAdsRequestPayload } from 'features/filters/Filters.mapper';

type UseAdsArgs = {
  onSuccess: () => void;
};

const useAds = (args: UseAdsArgs) => {
  const { onSuccess } = args;

  const { query } = useRouter();
  const { filtersData } = useFiltersState();
  const { setAds, setIsLoading } = useSearchPageDispatch();

  const handleError = (error: Error, payload: IGetAdsRequest) => {
    rg4js('send', {
      error: new Error('Error handling getAds'),
      tags: [PAGE.SEARCH],
      customData: {
        message: error.message || 'client_error',
        payload,
      },
    });
    fireToast({
      type: 'ERROR',
      text: 'Oops! Something went wrong, please try again later',
    });
    setIsLoading(false);
  };

  const getAds = async () => {
    setIsLoading(true);

    const getAdsPayload = mapGetAdsRequestPayload({ query, filtersData });

    try {
      const {
        data: {
          ads,
          spotlights,
          paging,
          seoData,
          dataTargetingProps: { dfp, gtm },
          breadcrumbs,
          recommendations,
        },
      } = await searchPageApi.getAdsV2(
        getAdsPayload,
        undefined,
        API_CLIENT_TIMEOUT,
      );

      setAds({
        ads,
        spotlights,
        seoData,
        dfp,
        gtm,
        breadcrumbs,
        recommendations,
        paging,
      });

      onSuccess();
    } catch (error) {
      handleError(error, getAdsPayload);
    }
  };

  return {
    getAds,
  };
};

export { useAds };
